import React, { useState, useEffect } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import {
  Box,
  Container,
  Heading,
  Text,
  Button,
  Card,
  CardHeader,
  CardBody,
  Divider,
  useToast,
  useMediaQuery,
  VStack,
  HStack,
  FormControl,
  FormLabel,
  Input,
  Textarea,
  Select,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  NumberIncrementStepper,
  NumberDecrementStepper,
} from "@chakra-ui/react";
import { FaCheck, FaInfoCircle, FaDollarSign, FaMapMarkerAlt, FaImage } from "react-icons/fa";
import ReactLoading from "react-loading";
import MultipleImageUploadComponent from "./SellFormComponents/multiple-image-upload.component";
import SelectLocation from "./SellFormComponents/SelectLocation";
import { API_BASE_URL } from "./config/api_keys";

export default function EditProduct() {
  const { id } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const toast = useToast();
  const [isMobile] = useMediaQuery("(max-width: 768px)");
  
  // Get product data from location state or fetch it
  const initialProduct = location.state?.product || null;
  
  const [loading, setLoading] = useState(!initialProduct);
  const [submitting, setSubmitting] = useState(false);
  const [product, setProduct] = useState(initialProduct);
  const [formData, setFormData] = useState({
    title: "",
    description: "",
    price: "",
    priceUnit: "kg",
    quantity: "",
    unit: "kg",
    cultivationMethod: "",
    harvestDate: "",
    quality: "",
    variety: "",
    age: "",
    breed: "",
    address: {},
    location: {},
  });
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [existingImages, setExistingImages] = useState([]);

  // Fetch product data if not provided in location state
  useEffect(() => {
    if (!initialProduct && id) {
      const fetchProduct = async () => {
        try {
          const token = localStorage.getItem("authToken");
          const response = await axios.get(`${API_BASE_URL}/previewad/${id}`, {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          });
          setProduct(response.data.product);
          setLoading(false);
        } catch (error) {
          console.error("Error fetching product:", error);
          toast({
            title: "Error",
            description: "Failed to load product data.",
            status: "error",
            duration: 3000,
            isClosable: true,
          });
          navigate("/myads");
        }
      };
      fetchProduct();
    } else if (initialProduct) {
      setLoading(false);
    }
  }, [id, initialProduct, navigate, toast]);

  // Initialize form data when product is loaded
  useEffect(() => {
    if (product) {
      // Extract agriculture details from the nested object
      const agricultureDetails = product.agricultureDetails || {};
      
      // Get quantity from agricultureDetails first, then fallback to product.quantity
      const quantity = agricultureDetails.quantity !== undefined ? agricultureDetails.quantity : 
                      product.quantity !== undefined ? product.quantity : "";
      
      // Get unit from agricultureDetails first, then fallback to product.unit
      const unit = agricultureDetails.unit !== undefined ? agricultureDetails.unit : 
                   product.unit !== undefined ? product.unit : "kg";
      
      // Get harvestDate and format it properly
      const harvestDate = agricultureDetails.harvestDate || product.harvestDate;
      const formattedHarvestDate = harvestDate ? new Date(harvestDate).toISOString().split('T')[0] : "";
      
      // Extract address and location data
      const addressData = product.address || {};
      const locationData = product.location || {};
      
      setFormData({
        title: product.title || "",
        description: product.description || "",
        price: product.price || "",
        priceUnit: product.priceUnit || "kg",
        quantity: quantity,
        unit: unit,
        cultivationMethod: agricultureDetails.cultivationMethod || product.cultivationMethod || "",
        harvestDate: formattedHarvestDate,
        quality: agricultureDetails.quality || product.quality || "",
        variety: agricultureDetails.variety || product.variety || "",
        age: agricultureDetails.age || product.age || "",
        breed: agricultureDetails.breed || product.breed || "",
        address: addressData,
        location: locationData,
      });
      
      // Set existing images
      if (product.images && product.images.length > 0) {
        setExistingImages(product.images);
      } else {
        // Fallback to productpic fields
        const pics = [];
        for (let i = 1; i <= 12; i++) {
          const picField = `productpic${i}`;
          if (product[picField]) {
            pics.push(product[picField]);
          }
        }
        setExistingImages(pics);
      }
    }
  }, [product]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handlePriceChange = (value) => {
    setFormData(prev => ({
      ...prev,
      price: value
    }));
  };

  const handleQuantityChange = (value) => {
    setFormData(prev => ({
      ...prev,
      quantity: value
    }));
  };

  const handleFileChange = (file) => {
    setUploadedFiles(prev => [...prev, file]);
  };

  const handleAddressChange = (data) => {
    const { location = {}, address = {} } = data || {};
    setFormData(prev => ({
      ...prev,
      address,
      location
    }));
  };

  const removeExistingImage = (index) => {
    setExistingImages(prev => prev.filter((_, i) => i !== index));
  };

  const removeNewImage = (index) => {
    setUploadedFiles(prev => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      // Upload new images if any
      let uploadedImageUrls = [...existingImages];
      
      if (uploadedFiles.length > 0) {
        const imageUrls = await Promise.all(
          uploadedFiles.map(async (file) => {
            const formData = new FormData();
            formData.append("image", file);
            const token = localStorage.getItem('authToken');
            
            const response = await axios.post(
              `${API_BASE_URL}/upload-product-image`,
              formData,
              {
                headers: {
                  'Authorization': `Bearer ${token}`,
                  'Content-Type': 'multipart/form-data',
                },
              }
            );
            return response.data.imageUrl;
          })
        );
        uploadedImageUrls = [...existingImages, ...imageUrls];
      }

      // Prepare data for update (only send fields that have values)
      const updateData = {
        uploadedFiles: uploadedImageUrls,
        name: localStorage.getItem("authname") || "",
        image: localStorage.getItem("authpicture") || "",
        username: localStorage.getItem("authname") || "",
        catagory: product.catagory,
        subcatagory: product.subcatagory,
      };
      
      // Only add fields to updateData if they have values
      if (formData.title) updateData.title = formData.title;
      if (formData.description) updateData.description = formData.description;
      if (formData.price) updateData.price = formData.price;
      if (formData.priceUnit) updateData.priceUnit = formData.priceUnit;
      if (formData.quantity) updateData.quantity = formData.quantity;
      if (formData.unit) updateData.unit = formData.unit;
      if (formData.cultivationMethod) updateData.cultivationMethod = formData.cultivationMethod;
      if (formData.harvestDate) updateData.harvestDate = formData.harvestDate;
      if (formData.quality) updateData.quality = formData.quality;
      if (formData.variety) updateData.variety = formData.variety;
      if (formData.age) updateData.age = formData.age;
      if (formData.breed) updateData.breed = formData.breed;
      if (formData.address && Object.keys(formData.address).length > 0) updateData.address = formData.address;
      if (formData.location && Object.keys(formData.location).length > 0) updateData.location = formData.location;

      const token = localStorage.getItem('authToken');
      
      // Update the product
      await axios.put(`${API_BASE_URL}/myads_update/${id}`, updateData, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      toast({
        title: "Product Updated",
        description: "Your product has been successfully updated.",
        status: "success",
        duration: 3000,
        isClosable: true,
      });
      
      // Navigate back to my ads
      navigate("/myads");
    } catch (error) {
      console.error("Error updating product:", error);
      toast({
        title: "Error",
        description: error.response?.data?.error || "Failed to update product.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    }
    
    setSubmitting(false);
  };

  if (loading) {
    return (
      <Box 
        display="flex" 
        justifyContent="center" 
        alignItems="center" 
        minHeight="60vh"
        flexDirection="column"
      >
        <ReactLoading type="spin" color="green" height={"50px"} width={"50px"} />
        <Text mt={4} fontSize="lg">Loading product data...</Text>
      </Box>
    );
  }

  return (
    <Container maxW={isMobile ? "container.sm" : "container.md"} py={6}>
      <Card w="100%" overflow="hidden" boxShadow="md" borderRadius="lg">
        <CardHeader bg="green.500" color="white" py={4}>
          <Heading size="md" textAlign="center">
            Edit Product
          </Heading>
        </CardHeader>
        
        <CardBody p={6}>
          <form onSubmit={handleSubmit}>
            <VStack spacing={6} align="stretch">
              {/* Product Details */}
              <Box>
                <HStack mb={2}>
                  <FaInfoCircle color="green" />
                  <Heading size="sm">Product Details</Heading>
                </HStack>
                <FormControl>
                  <FormLabel>Title</FormLabel>
                  <Input
                    name="title"
                    value={formData.title}
                    onChange={handleInputChange}
                    placeholder="Enter product title"
                  />
                </FormControl>
                
                <FormControl mt={4}>
                  <FormLabel>Description</FormLabel>
                  <Textarea
                    name="description"
                    value={formData.description}
                    onChange={handleInputChange}
                    placeholder="Enter product description"
                    rows={3}
                  />
                </FormControl>
              </Box>
              
              <Divider />
              
              {/* Price */}
              <Box>
                <HStack mb={2}>
                  <FaDollarSign color="green" />
                  <Heading size="sm">Price</Heading>
                </HStack>
                <HStack>
                  <FormControl>
                    <FormLabel>Price</FormLabel>
                    <NumberInput 
                      min={0} 
                      value={formData.price || ""}
                      onChange={handlePriceChange}
                      placeholder="Enter price"
                    >
                      <NumberInputField
                        name="price"
                      />
                    </NumberInput>
                  </FormControl>
                  
                  <FormControl>
                    <FormLabel>Unit</FormLabel>
                    <Select
                      name="priceUnit"
                      value={formData.priceUnit}
                      onChange={handleInputChange}
                    >
                      <option value="kg">Per kg</option>
                      <option value="liter">Per liter</option>
                      <option value="piece">Per piece</option>
                      <option value="dozen">Per dozen</option>
                      <option value="bundle">Per bundle</option>
                    </Select>
                  </FormControl>
                </HStack>
              </Box>
              
              <Divider />
              
              {/* Quantity */}
              <Box>
                <HStack mb={2}>
                  <FaInfoCircle color="green" />
                  <Heading size="sm">Quantity</Heading>
                </HStack>
                <HStack>
                  <FormControl>
                    <FormLabel>Quantity</FormLabel>
                    <NumberInput 
                      min={0} 
                      value={formData.quantity || ""}
                      onChange={handleQuantityChange}
                      placeholder="Enter quantity"
                    >
                      <NumberInputField
                        name="quantity"
                      />
                    </NumberInput>
                  </FormControl>
                  
                  <FormControl>
                    <FormLabel>Unit</FormLabel>
                    <Select
                      name="unit"
                      value={formData.unit}
                      onChange={handleInputChange}
                    >
                      <option value="kg">kg</option>
                      <option value="liter">liter</option>
                      <option value="piece">piece</option>
                      <option value="dozen">dozen</option>
                      <option value="bundle">bundle</option>
                    </Select>
                  </FormControl>
                </HStack>
              </Box>
              
              <Divider />
              
              {/* Location */}
              <Box>
                <HStack mb={2}>
                  <FaMapMarkerAlt color="green" />
                  <Heading size="sm">Location</Heading>
                </HStack>
                <SelectLocation 
                  onAddressSelect={handleAddressChange} 
                  initialLocation={formData.location}
                  initialAddress={formData.address}
                />
              </Box>
              
              <Divider />
              
              {/* Images */}
              <Box>
                <HStack mb={2}>
                  <FaImage color="green" />
                  <Heading size="sm">Product Images</Heading>
                </HStack>
                
                {/* Existing Images */}
                {existingImages.length > 0 && (
                  <Box mb={4}>
                    <Text mb={2}>Existing Images:</Text>
                    <HStack spacing={2} overflowX="auto">
                      {existingImages.map((img, index) => (
                        <Box key={index} position="relative">
                          <img 
                            src={img} 
                            alt={`Existing ${index + 1}`} 
                            style={{ width: "100px", height: "100px", objectFit: "cover", borderRadius: "4px" }}
                          />
                          <Button
                            size="xs"
                            colorScheme="red"
                            position="absolute"
                            top="0"
                            right="0"
                            onClick={() => removeExistingImage(index)}
                          >
                            X
                          </Button>
                        </Box>
                      ))}
                    </HStack>
                  </Box>
                )}
                
                {/* New Images */}
                {uploadedFiles.length > 0 && (
                  <Box mb={4}>
                    <Text mb={2}>New Images:</Text>
                    <HStack spacing={2} overflowX="auto">
                      {uploadedFiles.map((file, index) => (
                        <Box key={index} position="relative">
                          <img 
                            src={URL.createObjectURL(file)} 
                            alt={`New ${index + 1}`} 
                            style={{ width: "100px", height: "100px", objectFit: "cover", borderRadius: "4px" }}
                          />
                          <Button
                            size="xs"
                            colorScheme="red"
                            position="absolute"
                            top="0"
                            right="0"
                            onClick={() => removeNewImage(index)}
                          >
                            X
                          </Button>
                        </Box>
                      ))}
                    </HStack>
                  </Box>
                )}
                
                <MultipleImageUploadComponent onFileSelect={handleFileChange} />
              </Box>
              
              <Divider />
              
              {/* Submit Button */}
              <HStack justifyContent="space-between">
                <Button
                  variant="outline"
                  colorScheme="gray"
                  onClick={() => navigate("/myads")}
                >
                  Cancel
                </Button>
                
                <Button
                  colorScheme="green"
                  type="submit"
                  isLoading={submitting}
                  leftIcon={<FaCheck />}
                >
                  Update Product
                </Button>
              </HStack>
            </VStack>
          </form>
        </CardBody>
      </Card>
    </Container>
  );
}